import {
   Stack,
   ContentCard,
   Group,
   Radio,
   Grid,
   Button,
   Skeleton,
} from '@forwardflow/ui-v2'
import {
   Building01Icon,
   UsersIcon,
   User01Icon,
   ChevronRightIcon,
} from '@forwardflow/icons'
import { useSubscriptionPackageLists } from '@forwardflow/hooks/customHooks/subscription'
import { useEffect, useState } from 'react'
import cn from 'classnames'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { SelectedTierCard } from '..'
import { motion } from 'framer-motion'
import { useAppStore } from '@forwardflow/store'

const iconMap = {
   'Solo Agent': User01Icon,
   'Small Team (2-10 agents)': UsersIcon,
   'Enterprise (Unlimited users)': Building01Icon,
}

const NumberOfUsers = () => {
   const { onboardingData, setSelectedTier } = useOnboardingStoreV2()
   const { selectedTier, isOnBoardingCompleted, isSubscriptionActivated } =
      onboardingData
   const isDisabled = isOnBoardingCompleted || isSubscriptionActivated

   const handleNumberOfUsersChange = (value: number) => {
      if (
         value < (selectedTier?.minSeats ?? 1) ||
         value > (selectedTier?.maxSeats ?? 100)
      )
         return
      setSelectedTier({
         includedSeats: value,
      })
   }

   return (
      <motion.div
         animate={{ opacity: 1 }}
         transition={{ duration: 0.3, ease: 'easeInOut' }}
         initial={{ opacity: 0.7 }}
         className="bg-white/2 p-4 rounded-lg border border-white/20"
      >
         <Group justify="space-between">
            <p className="text-white text-base">Select the number of users</p>
            <Group>
               <span
                  className="cursor-pointer bg-white/20 rounded-lg hover:bg-white/30 h-8 w-8 flex items-center justify-center"
                  onClick={(e) => {
                     e.stopPropagation()
                     if (isDisabled) return
                     handleNumberOfUsersChange(
                        (selectedTier?.includedSeats ?? 1) - 1
                     )
                  }}
               >
                  <div>-</div>
               </span>
               <p className="text-white text-base">
                  {selectedTier?.includedSeats}
               </p>
               <span
                  className="cursor-pointer bg-white/20 rounded-lg hover:bg-white/30 h-8 w-8 flex items-center justify-center"
                  onClick={(e) => {
                     e.stopPropagation()
                     if (isDisabled) return
                     handleNumberOfUsersChange(
                        (selectedTier?.includedSeats ?? 1) + 1
                     )
                  }}
               >
                  <div>+</div>
               </span>
            </Group>
         </Group>
      </motion.div>
   )
}

export const PlanSelection = () => {
   const {
      onboardingData,
      getNextStep,
      setOrganizationId,
      setSelectedTier,
      setPlanPeriod,
   } = useOnboardingStoreV2()
   const { setCurrentOrganization } = useAppStore()
   const {
      email,
      selectedTier,
      planPeriod,
      isSubscriptionActivated,
      isOnBoardingCompleted,
   } = onboardingData
   const [isOnboardingStarting, setIsOnboardingStarting] = useState(false)
   const { data, isLoading } = useSubscriptionPackageLists({
      page: 1,
      limit: 10,
   })
   const isDisabled = isOnBoardingCompleted || isSubscriptionActivated

   const planCategories = [
      {
         label: 'Monthly',
         value: 'monthly',
         description: 'Pay month to month',
      },
      {
         label: 'Yearly',
         value: 'yearly',
         description: 'Save 20% with an annual subscription.',
      },
   ]

   const pricingPlans = data?.pricingTiers.map((tier) => ({
      ...tier,
      icon: iconMap[tier.tierName as keyof typeof iconMap],
   }))

   useEffect(() => {
      if (!selectedTier && pricingPlans && pricingPlans?.length > 0) {
         setSelectedTier(pricingPlans[0])
      }
   }, [pricingPlans, selectedTier, setSelectedTier])

   // *** handlers ***
   const handleStartOnboarding = async () => {
      // if (
      //    isOperatorEmail(email || '') &&
      //    ONBOARDING_DUMMY_ORGANIZATION_ID
      // ) {
      //    getNextStep()
      //    return
      // }
      try {
         if (!email) {
            return
         }
         setIsOnboardingStarting(true)
         const data = await OnboardingV2Service.startOnboarding({
            email,
         })
         const organizationId = data?.data?.id
         if (data?.success && organizationId) {
            setCurrentOrganization(organizationId)
            setOrganizationId(organizationId)
            getNextStep()
         }
      } catch (error) {
         console.error(error)
      } finally {
         setIsOnboardingStarting(false)
      }
   }

   return (
      <Stack gap={64}>
         <ContentCard className="space-y-2">
            <p className="text-white font-medium">
               Welcome to Forward Flow! Pick the perfect plan for your team
            </p>
            <p className="text-[#99A1AF] text-sm font-medium">
               Choose a billing cycle and a plan that fits your team. You can
               switch anytime from your account settings.
            </p>
         </ContentCard>
         <ContentCard className="!space-y-8">
            <Stack>
               <p className="text-white font-medium text-base">
                  What billing type do you want?
               </p>
               {planCategories.map((category) => {
                  const isSelected = planPeriod === category.value
                  return (
                     <button
                        disabled={isSubscriptionActivated}
                        className="text-left w-full"
                        key={category.value}
                        onClick={() =>
                           setPlanPeriod(category.value as 'monthly' | 'yearly')
                        }
                     >
                        <Grid
                           className={cn(
                              'rounded-lg border border-white/20 bg-white/2 p-4 cursor-pointer',
                              {
                                 'border-white/40 bg-white/5': isSelected,
                              }
                           )}
                        >
                           <Grid.Col span={2}>
                              <Group wrap="nowrap">
                                 <Radio
                                    disabled={isDisabled}
                                    key={category.value}
                                    value={category.value}
                                    checked={isSelected}
                                    readOnly
                                 />
                                 <p
                                    className={cn('font-medium text-base', {
                                       'text-white': isSelected,
                                       'text-[#99A1AF]': !isSelected,
                                    })}
                                 >
                                    {category.label}
                                 </p>
                              </Group>
                           </Grid.Col>
                           <Grid.Col span={1} />
                           <Grid.Col span={9}>
                              <p
                                 className={cn(
                                    'text-[#99A1AF] text-base font-medium',
                                    {
                                       'text-[#99A1AF]': !isSelected,
                                       'text-white': isSelected,
                                    }
                                 )}
                              >
                                 {category.description}
                              </p>
                           </Grid.Col>
                        </Grid>
                     </button>
                  )
               })}
            </Stack>

            <Stack>
               <p className="text-white font-medium text-base">
                  How many users will be using Forward Flow?
               </p>
               {isLoading ? (
                  <Stack>
                     <Skeleton height={60} color="blue-500" radius="md" />
                     <Skeleton height={60} color="blue-500" radius="md" />
                     <Skeleton height={60} color="blue-500" radius="md" />
                  </Stack>
               ) : (
                  <Stack>
                     {pricingPlans?.map((plan) => {
                        const Icon = plan?.icon as React.ElementType
                        const isSelected = selectedTier?.id === plan?.id
                        const price =
                           planPeriod === 'monthly'
                              ? plan?.monthlyPrice
                              : plan?.annualPrice
                        const period =
                           planPeriod === 'monthly' ? 'month' : 'year'
                        const priceText = `$${price?.toLocaleString('en-US')}${
                           plan?.tierUnit === 'per_user_period' ? '/user' : ''
                        }`
                        return (
                           <motion.button
                              key={plan?.id}
                              disabled={isSubscriptionActivated}
                              className={cn('opacity-100', {
                                 'opacity-70': !isSelected,
                              })}
                              onClick={() => {
                                 if (!isSelected) {
                                    setSelectedTier(plan)
                                 }
                              }}
                           >
                              <Stack
                                 gap={24}
                                 className={cn(
                                    'rounded-lg border border-white/20 bg-white/2 p-4 cursor-pointer',
                                    {
                                       'border-white/40 bg-white/5': isSelected,
                                    }
                                 )}
                              >
                                 <Group justify="space-between">
                                    <Group>
                                       <Radio
                                          key={plan?.id}
                                          disabled={isSubscriptionActivated}
                                          value={plan?.id}
                                          checked={isSelected}
                                          className="cursor-pointer"
                                          readOnly
                                       />
                                       <Group>
                                          <Icon
                                             color="#A684FF"
                                             className={cn('w-6', {
                                                'opacity-100': isSelected,
                                                'opacity-50': !isSelected,
                                             })}
                                          />
                                          <p
                                             className={cn(
                                                'font-medium text-base',
                                                {
                                                   'text-white': isSelected,
                                                   'text-[#99A1AF]':
                                                      !isSelected,
                                                }
                                             )}
                                          >
                                             {plan?.tierName}
                                          </p>
                                       </Group>
                                    </Group>
                                    <p
                                       className={cn(
                                          'font-semibold text-base',
                                          {
                                             'text-white': isSelected,
                                             'text-[#99A1AF]': !isSelected,
                                          }
                                       )}
                                    >
                                       {priceText}
                                       <span className="text-[#99A1AF]">
                                          /{period}
                                       </span>
                                    </p>
                                 </Group>
                                 {plan?.tierUnit === 'per_user_period' &&
                                    isSelected && <NumberOfUsers />}
                              </Stack>
                           </motion.button>
                        )
                     })}
                  </Stack>
               )}
            </Stack>
         </ContentCard>
         {selectedTier && (
            <ContentCard
               className="bg-[#A684FF]/10"
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
               <Stack gap={24}>
                  <SelectedTierCard />
                  <p className="text-[#99A1AF] text-sm font-medium text-center">
                     A $200 Build Out Fee Applies which can be used as Credits
                     for Calls and Texts
                  </p>
                  <Button
                     className="w-full"
                     onClick={handleStartOnboarding}
                     disabled={!selectedTier}
                     loading={isOnboardingStarting}
                  >
                     {isSubscriptionActivated
                        ? 'Continue'
                        : 'Confirm Plan and Continue'}{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Stack>
            </ContentCard>
         )}
      </Stack>
   )
}
